import React, { useState, useEffect } from 'react';
import { SiteHeader, SiteFooter } from '@/components/SiteLayout';
import {
  ChevronRight, BookOpen, Zap, AlertTriangle, CheckCircle,
  Copy, Check, ArrowRight, Clock, Ban, DollarSign, Webhook,
  Lock, RefreshCcw, Search, ChevronDown, FileText, Tag
} from 'lucide-react';

const SIDEBAR = [
  { id: 'overview', label: 'Overview', icon: BookOpen },
  { id: 'payment-tolerance', label: 'Payment Tolerance (%)', icon: Zap },
  { id: 'matching-logic', label: 'Exact Matching Logic', icon: Search },
  { id: 'scenario-exact', label: 'Scenario: Exact Payment', icon: CheckCircle },
  { id: 'scenario-partial', label: 'Scenario: Partial Payment', icon: AlertTriangle },
  { id: 'scenario-underpaid', label: 'Scenario: Underpayment', icon: Ban },
  { id: 'scenario-late', label: 'Scenario: Late Payment', icon: Clock },
  { id: 'scenario-overpaid', label: 'Scenario: Overpayment', icon: DollarSign },
  { id: 'status-lifecycle', label: 'Invoice Status Lifecycle', icon: RefreshCcw },
  { id: 'webhooks', label: 'Webhooks & Notifications', icon: Webhook },
  { id: 'best-practices', label: 'Best Practices', icon: BookOpen },
  { id: 'security', label: 'Security Considerations', icon: Lock },
  { id: 'reference-field', label: 'Reference Field', icon: FileText },
];

function CodeBlock({ code, lang = 'json', title }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="rounded-xl border border-border overflow-hidden my-4">
      {title && (
        <div className="flex items-center justify-between px-4 py-2 bg-muted/50 border-b border-border">
          <span className="text-xs font-mono text-muted-foreground">{title}</span>
          <button onClick={handleCopy} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
            {copied ? <><Check className="h-3 w-3 text-emerald-400" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
          </button>
        </div>
      )}
      <pre className="p-4 overflow-x-auto text-sm bg-[#0d1117] text-gray-300" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
        <code>{code}</code>
      </pre>
    </div>
  );
}

function InfoBox({ type = 'info', title, children }) {
  const styles = {
    info: 'border-primary/30 bg-primary/5',
    warning: 'border-amber-500/30 bg-amber-500/5',
    success: 'border-emerald-500/30 bg-emerald-500/5',
    danger: 'border-red-500/30 bg-red-500/5',
  };
  const icons = { info: Zap, warning: AlertTriangle, success: CheckCircle, danger: Ban };
  const Icon = icons[type];
  const textColor = { info: 'text-primary', warning: 'text-amber-400', success: 'text-emerald-400', danger: 'text-red-400' };
  return (
    <div className={`rounded-xl border p-4 my-4 ${styles[type]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-4 w-4 ${textColor[type]}`} />
        <span className={`text-sm font-semibold ${textColor[type]}`}>{title}</span>
      </div>
      <div className="text-sm text-muted-foreground leading-relaxed">{children}</div>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 pb-12 mb-12 border-b border-border last:border-0">
      <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-6 text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h2>
      <div className="prose-content space-y-4 text-[15px] text-muted-foreground leading-relaxed">{children}</div>
    </section>
  );
}

function StepFlow({ steps }) {
  return (
    <div className="my-6 space-y-0">
      {steps.map((step, i) => (
        <div key={i} className="flex gap-4">
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">{i + 1}</div>
            {i < steps.length - 1 && <div className="w-px h-full bg-border min-h-[24px]" />}
          </div>
          <div className="pb-6">
            <p className="text-sm font-semibold text-foreground">{step.title}</p>
            <p className="text-sm text-muted-foreground mt-0.5">{step.desc}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

const DocsPage = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    document.title = 'Documentation | CoinPayUp';
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
          break;
        }
      }
    }, { rootMargin: '-100px 0px -60% 0px', threshold: 0 });
    SIDEBAR.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="docs-page">
      <SiteHeader />

      {/* Page Header */}
      <div className="border-b border-border bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
            <span>Docs</span><ChevronRight className="h-3.5 w-3.5" /><span>Guides</span><ChevronRight className="h-3.5 w-3.5" /><span className="text-foreground">Payment Verification</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Payment Tolerance & Verification
          </h1>
          <p className="text-base text-muted-foreground mt-2 max-w-2xl">
            Complete guide to how CoinPayUp verifies blockchain payments, handles tolerance thresholds, and manages every payment scenario.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 flex gap-8 relative">
        {/* Mobile sidebar toggle */}
        <button className="lg:hidden fixed bottom-6 right-6 z-40 bg-primary text-white px-4 py-2.5 rounded-full shadow-lg flex items-center gap-2 text-sm font-medium"
          onClick={() => setSidebarOpen(!sidebarOpen)}>
          <BookOpen className="h-4 w-4" /> Sections <ChevronDown className={`h-3.5 w-3.5 transition-transform ${sidebarOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Sidebar */}
        <aside className={`w-64 flex-shrink-0 lg:block ${sidebarOpen ? 'fixed inset-0 z-30 bg-background/95 backdrop-blur-xl lg:relative lg:bg-transparent p-6 pt-20 lg:p-0 overflow-y-auto' : 'hidden'}`}>
          <nav className="lg:sticky lg:top-24 py-8 space-y-1" data-testid="docs-sidebar">
            {SIDEBAR.map(item => (
              <button key={item.id} onClick={() => scrollTo(item.id)} data-testid={`sidebar-${item.id}`}
                className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === item.id ? 'bg-primary/10 text-primary font-semibold' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}>
                <item.icon className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{item.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 py-8 lg:py-12 max-w-3xl" data-testid="docs-content">

          {/* OVERVIEW */}
          <Section id="overview" title="Overview">
            <p>
              CoinPayUp provides a robust payment verification system for blockchain payments — ensuring payments are real, irreversible, and exactly what they claim to be.
            </p>
            <p>This guide covers:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Payment tolerance configuration for handling real-world payment variations</li>
              <li>Exact matching logic between invoice amounts and received funds</li>
              <li>All payment scenarios: exact, partial, underpaid, late, and overpaid</li>
              <li>Invoice status lifecycle and webhook event reference</li>
              <li>Security considerations and best practices</li>
            </ul>
          </Section>

          {/* PAYMENT TOLERANCE */}
          <Section id="payment-tolerance" title="Payment Tolerance (%)">
            <p>Payment tolerance allows merchants to accept payments that are <strong className="text-foreground">slightly less</strong> than the invoice amount. This accounts for real-world variations that commonly occur in crypto payments.</p>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Why Tolerance Exists</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-4">
              {[
                { title: 'Gas fee deductions', desc: 'Some wallets deduct gas from the transfer amount rather than the sender\'s balance' },
                { title: 'Exchange withdrawal fees', desc: 'Crypto exchanges deduct fixed fees when users withdraw to external wallets' },
                { title: 'Wallet rounding', desc: 'Some wallets round amounts to fewer decimal places' },
                { title: 'Price slippage', desc: 'Minor amount differences when converting between currencies' },
              ].map(r => (
                <div key={r.title} className="p-3 rounded-lg bg-muted/50 border border-border">
                  <p className="text-sm font-semibold text-foreground">{r.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{r.desc}</p>
                </div>
              ))}
            </div>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Default Behavior</h3>
            <p>By default, tolerance is <strong className="text-foreground">disabled</strong>. When disabled, only exact payments (or overpayments) are accepted as fully paid. Any underpayment results in a <code className="px-1.5 py-0.5 rounded bg-muted text-foreground text-xs font-mono">partially_paid</code> status.</p>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Configuring Tolerance</h3>
            <CodeBlock title="PUT /api/settings/payment-tolerance" code={`{
  "enabled": true,
  "percentage": 2.0
}`} />
            <p>Tolerance can be set between <strong className="text-foreground">0%</strong> and <strong className="text-foreground">10%</strong>. This determines the minimum acceptable payment as a percentage of the invoice amount.</p>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Tolerance Calculation</h3>
            <CodeBlock title="Tolerance Formula" code={`minimum_acceptable = invoice_amount * (1 - tolerance_percentage / 100)

Example: $100 invoice with 2% tolerance
minimum_acceptable = 100 * (1 - 2/100) = 100 * 0.98 = $98.00

Accepted range: $98.00 — $101.00 (paid)
Above $101.00: overpaid
Below $98.00: partially_paid`} />

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Example: $100 Invoice, 2% Tolerance</h3>
            <div className="overflow-x-auto my-4">
              <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Amount Received</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Status</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Explanation</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['$100.00', 'paid', 'Exact match', 'bg-emerald-500/10'],
                    ['$99.50', 'paid', 'Within 2% tolerance ($98.00 min)', 'bg-emerald-500/10'],
                    ['$98.00', 'paid', 'Exactly at tolerance boundary', 'bg-emerald-500/10'],
                    ['$97.99', 'partially_paid', 'Below tolerance — 0.01 short', 'bg-amber-500/10'],
                    ['$50.00', 'partially_paid', 'Partial payment — $50.00 remaining', 'bg-amber-500/10'],
                    ['$101.01', 'overpaid', 'Exceeds invoice by > 1%', 'bg-blue-500/10'],
                    ['$0.00', 'unpaid', 'No payment amount detected', 'bg-red-500/10'],
                  ].map(([amount, status, explanation, bg], i) => (
                    <tr key={i} className={bg}>
                      <td className="px-4 py-2 border-b border-border font-mono text-foreground">{amount}</td>
                      <td className="px-4 py-2 border-b border-border"><code className="px-1.5 py-0.5 rounded bg-muted text-xs font-mono">{status}</code></td>
                      <td className="px-4 py-2 border-b border-border text-muted-foreground">{explanation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Recommended Tolerance Values</h3>
            <div className="overflow-x-auto my-4">
              <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                <thead><tr className="bg-muted/50">
                  <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Use Case</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Recommended</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Rationale</th>
                </tr></thead>
                <tbody>
                  {[
                    ['Stablecoins (USDT, USDC)', '1–2%', 'Covers exchange withdrawal fees'],
                    ['Volatile tokens (ETH, BNB)', '2–5%', 'Accounts for price movement during transfer'],
                    ['High-value invoices (>$1,000)', '0.5–1%', 'Tighter tolerance to limit loss'],
                    ['Micropayments (<$10)', '3–5%', 'Higher tolerance to avoid friction'],
                  ].map(([useCase, rec, rationale], i) => (
                    <tr key={i}>
                      <td className="px-4 py-2 border-b border-border text-foreground">{useCase}</td>
                      <td className="px-4 py-2 border-b border-border font-mono text-primary">{rec}</td>
                      <td className="px-4 py-2 border-b border-border text-muted-foreground">{rationale}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <InfoBox type="warning" title="Security Consideration">
              Higher tolerance values increase the risk of accepting significantly underpaid invoices. For invoices over $500, we recommend keeping tolerance at 1% or below.
            </InfoBox>
          </Section>

          {/* EXACT MATCHING LOGIC */}
          <Section id="matching-logic" title="Exact Payment Matching Logic">
            <p>CoinPayUp uses a precise, multi-step algorithm to match blockchain payments to invoices.</p>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Key Concepts</h3>
            <div className="overflow-x-auto my-4">
              <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                <thead><tr className="bg-muted/50">
                  <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Field</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Description</th>
                </tr></thead>
                <tbody>
                  {[
                    ['payable_amount', 'The exact amount the payer should send (may differ from base amount if round_up/round_down is used)'],
                    ['received_amount', 'Cumulative total of all confirmed payments for this invoice'],
                    ['remaining_amount', 'payable_amount - received_amount (never negative)'],
                    ['tolerance_min', 'payable_amount * (1 - tolerance_pct / 100) — the minimum to be considered "paid"'],
                  ].map(([field, desc], i) => (
                    <tr key={i}>
                      <td className="px-4 py-2 border-b border-border font-mono text-primary text-xs">{field}</td>
                      <td className="px-4 py-2 border-b border-border text-muted-foreground">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Decision Tree</h3>
            <CodeBlock title="Payment Status Resolution" code={`if received_total >= invoice_amount * 1.01:
    status = "overpaid"

elif received_total >= invoice_amount:
    status = "paid"

elif tolerance_enabled AND received_total >= tolerance_min:
    status = "paid"              # tolerance applied
    tolerance_applied = true

elif received_total > 0:
    status = "partially_paid"    # needs top-up

else:
    status = "unpaid"            # no payment detected`} />

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Multi-Transfer Aggregation</h3>
            <p>CoinPayUp supports <strong className="text-foreground">cumulative payments</strong>. If a payer sends partial payments across multiple transactions, each confirmed payment is recorded and the <code className="px-1.5 py-0.5 rounded bg-muted text-foreground text-xs font-mono">received_amount</code> is the sum of all confirmed payments for that invoice.</p>
            <CodeBlock title="Aggregation Example" code={`Invoice: 100 USDT

Payment 1: 60 USDT → received_total = 60 → status: "partially_paid"
Payment 2: 30 USDT → received_total = 90 → status: "partially_paid"
Payment 3: 10 USDT → received_total = 100 → status: "paid" ✓`} />

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Duplicate Payment Protection</h3>
            <p>CoinPayUp enforces two layers of deduplication:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong className="text-foreground">Per-invoice:</strong> The same transaction hash cannot be submitted twice for the same invoice</li>
              <li><strong className="text-foreground">Cross-invoice:</strong> A confirmed transaction hash cannot be reused for a different invoice — preventing double-spend fraud where a single payment is claimed against multiple invoices</li>
            </ul>
          </Section>

          {/* SCENARIO: EXACT PAYMENT */}
          <Section id="scenario-exact" title="Scenario A: Exact Payment (or Within Tolerance)">
            <InfoBox type="success" title="Happy Path">This is the standard successful payment flow.</InfoBox>
            <StepFlow steps={[
              { title: 'Invoice created', desc: 'Merchant creates invoice for 100 USDT via API or dashboard. The system generates a payable_amount (may include round_up adjustment).' },
              { title: 'Customer sends payment', desc: 'Customer sends 100 USDT to the invoice wallet address using any wallet or exchange.' },
              { title: 'Payment detected on-chain', desc: 'CoinPayUp\'s blockchain listener detects the Transfer event in real-time via WebSocket monitoring. Alternatively, the customer submits the TXID for manual verification.' },
              { title: 'Verification executed', desc: 'Transaction receipt is fetched. Transfer logs are parsed. Amount (100 USDT), recipient, and token are validated against the invoice.' },
              { title: 'Tolerance check applied', desc: 'received_total (100) >= invoice_amount (100). No tolerance needed — exact match.' },
              { title: 'Invoice marked paid', desc: 'Invoice status → paid. paid_at timestamp recorded. received_amount = 100, remaining_amount = 0.' },
              { title: 'Webhook triggered', desc: 'payment.confirmed and invoice.paid events dispatched to merchant\'s webhook endpoint with HMAC signature.' },
              { title: 'Customer redirected', desc: 'If merchant has configured a success_url, the checkout page redirects the customer after confirmation.' },
            ]} />

            <CodeBlock title="Response: Successful Verification" code={`{
  "status": "paid",
  "message": "Payment verified! 100.0 USDT confirmed with 12 block confirmations.",
  "tx_hash": "0xabc123...",
  "amount_received": 100.0,
  "received_total": 100.0,
  "remaining_amount": 0,
  "invoice_amount": 100.0,
  "currency": "USDT",
  "confirmations": 12,
  "from_address": "0xsender...",
  "tolerance_applied": false,
  "tolerance_percentage": null
}`} />
          </Section>

          {/* SCENARIO: PARTIAL */}
          <Section id="scenario-partial" title="Scenario B: Partial Payment">
            <InfoBox type="warning" title="Half-Paid Invoice">When a customer sends less than the required amount, CoinPayUp tracks the partial payment and keeps the invoice active for top-ups.</InfoBox>

            <h3 className="text-lg font-semibold text-foreground mt-6 mb-3">Numeric Example</h3>
            <CodeBlock title="Step-by-Step: $100 Invoice, Customer Sends $60" code={`Invoice created: 100 USDT
Payment tolerance: 2% (minimum = $98.00)

── Payment 1: 60 USDT ──────────────────────────
  received_total = 60.00
  remaining_amount = 40.00
  60.00 < 98.00 (tolerance_min) → status: "partially_paid"
  Webhook: invoice.underpaid (remaining: 40.00 USDT)

── Payment 2: 30 USDT (top-up) ─────────────────
  received_total = 60.00 + 30.00 = 90.00
  remaining_amount = 10.00
  90.00 < 98.00 → status: "partially_paid"
  Webhook: invoice.underpaid (remaining: 10.00 USDT)

── Payment 3: 10 USDT (final top-up) ───────────
  received_total = 90.00 + 10.00 = 100.00
  remaining_amount = 0.00
  100.00 >= 100.00 → status: "paid" ✓
  Webhook: payment.confirmed + invoice.paid
  Customer redirected to success_url`} />

            <StepFlow steps={[
              { title: 'Partial payment detected', desc: 'CoinPayUp detects 60 USDT transfer. This is below the tolerance minimum ($98.00).' },
              { title: 'Invoice marked partially_paid', desc: 'Status → partially_paid. received_amount = 60, remaining_amount = 40.' },
              { title: 'Checkout shows remaining balance', desc: 'The checkout page updates to show "Remaining: 40.00 USDT" with a new TXID input for the top-up.' },
              { title: 'Additional payments aggregated', desc: 'Each new payment is added to received_total. The invoice remains active until fully paid or expired.' },
              { title: 'Invoice becomes paid', desc: 'When received_total meets the threshold (exact or within tolerance), the status transitions to paid.' },
            ]} />
          </Section>

          {/* SCENARIO: UNDERPAID */}
          <Section id="scenario-underpaid" title="Scenario C: Underpayment Beyond Tolerance">
            <InfoBox type="danger" title="Payment Too Low">When a payment falls below the tolerance threshold with no follow-up, the invoice remains in a partially_paid state.</InfoBox>

            <CodeBlock title="Example: $100 Invoice, 2% Tolerance, Customer Sends $95" code={`Invoice: 100 USDT
Tolerance: 2% → minimum = $98.00

Customer sends: $95.00
received_total = $95.00
$95.00 < $98.00 → status: "partially_paid"
remaining_amount = $5.00

The invoice is NOT marked as paid.
The customer must send an additional $5.00 to complete the payment.`} />

            <h3 className="text-lg font-semibold text-foreground mt-6 mb-3">Merchant Options</h3>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong className="text-foreground">Wait for top-up:</strong> The invoice remains active. The customer can submit additional payments. CoinPayUp aggregates all confirmed transfers.</li>
              <li><strong className="text-foreground">Manual resolution:</strong> The merchant can manually mark the invoice as paid from the dashboard if they choose to accept the shortfall.</li>
              <li><strong className="text-foreground">Refund & re-issue:</strong> The merchant can refund the partial payment off-chain and create a new invoice.</li>
              <li><strong className="text-foreground">Increase tolerance:</strong> If this happens frequently, consider increasing the tolerance percentage in Settings.</li>
            </ul>
          </Section>

          {/* SCENARIO: LATE */}
          <Section id="scenario-late" title="Scenario D: Payment After Expiry">
            <InfoBox type="warning" title="Late Payment">When a customer sends payment after the invoice has expired, the invoice status remains expired — but the payment is still detected.</InfoBox>

            <StepFlow steps={[
              { title: 'Invoice expires', desc: 'The invoice reaches its expiry time. Status → expired. The checkout page shows an expiry message.' },
              { title: 'Customer sends payment anyway', desc: 'The payment arrives on-chain after the expiry time.' },
              { title: 'Payment still detected', desc: 'CoinPayUp\'s blockchain listener still detects the transfer. The payment is logged as an unmatched payment.' },
              { title: 'Merchant notified', desc: 'The merchant can see the late payment in their dashboard under unmatched payments. A webhook event may be dispatched depending on configuration.' },
              { title: 'Manual resolution required', desc: 'The merchant should contact the customer to arrange a refund or manually credit their account.' },
            ]} />

            <h3 className="text-lg font-semibold text-foreground mt-6 mb-3">Recommended Merchant Workflow</h3>
            <CodeBlock title="Handling Late Payments" code={`1. Monitor unmatched payments in your dashboard
2. Cross-reference with expired invoices by wallet address and amount
3. Contact the customer directly
4. Options:
   a. Refund the payment to the sender address
   b. Create a new invoice and mark it as paid manually
   c. Credit the amount to the customer's account
5. Document the resolution in your records`} />

            <InfoBox type="info" title="Prevention Tip">
              Set longer expiry times for invoices (60+ minutes) to reduce late payment incidents. You can configure this per-invoice via the <code className="px-1 py-0.5 rounded bg-muted text-foreground text-xs font-mono">expiry_minutes</code> parameter.
            </InfoBox>
          </Section>

          {/* SCENARIO: OVERPAID */}
          <Section id="scenario-overpaid" title="Scenario E: Overpayment">
            <p>When a customer sends <strong className="text-foreground">more than 1% above</strong> the invoice amount, CoinPayUp marks the invoice as <code className="px-1.5 py-0.5 rounded bg-muted text-foreground text-xs font-mono">overpaid</code>.</p>

            <CodeBlock title="Example: $100 Invoice, Customer Sends $105" code={`Invoice: 100 USDT
Customer sends: 105 USDT

received_total = 105.00
105.00 >= 100.00 * 1.01 (101.00) → status: "overpaid"
overpaid_by = 5.00 USDT

Webhook: invoice.overpaid (overpaid_by: 5.00)`} />

            <h3 className="text-lg font-semibold text-foreground mt-6 mb-3">Merchant Options</h3>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong className="text-foreground">Accept & fulfill:</strong> Treat the overpayment as a tip or goodwill. The invoice is fully settled.</li>
              <li><strong className="text-foreground">Partial refund:</strong> Refund the overpaid amount to the customer's wallet address (off-chain).</li>
              <li><strong className="text-foreground">Credit to account:</strong> Apply the excess as credit toward the customer's next purchase.</li>
            </ul>

            <InfoBox type="info" title="Overpayment Threshold">
              CoinPayUp uses a 1% threshold for overpayment detection. Payments between 100%–101% are considered normal (accounts for rounding). Above 101% triggers the <code className="px-1 py-0.5 rounded bg-muted text-foreground text-xs font-mono">overpaid</code> status.
            </InfoBox>
          </Section>

          {/* STATUS LIFECYCLE */}
          <Section id="status-lifecycle" title="Invoice Status Lifecycle">
            <div className="overflow-x-auto my-4">
              <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                <thead><tr className="bg-muted/50">
                  <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Status</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Description</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Transitions To</th>
                </tr></thead>
                <tbody>
                  {[
                    ['pending', 'Invoice created, awaiting payment', 'paid, partially_paid, expired, cancelled'],
                    ['partially_paid', 'Payment received but below required amount', 'paid, overpaid, expired'],
                    ['paid', 'Full payment confirmed (exact or within tolerance)', 'Terminal state'],
                    ['overpaid', 'Payment exceeds invoice by > 1%', 'Terminal state'],
                    ['expired', 'Invoice passed its expiry time without full payment', 'Terminal state'],
                    ['cancelled', 'Manually cancelled by merchant', 'Terminal state'],
                  ].map(([status, desc, transitions], i) => (
                    <tr key={i}>
                      <td className="px-4 py-2.5 border-b border-border"><code className="px-1.5 py-0.5 rounded bg-muted text-xs font-mono text-foreground">{status}</code></td>
                      <td className="px-4 py-2.5 border-b border-border text-muted-foreground">{desc}</td>
                      <td className="px-4 py-2.5 border-b border-border text-muted-foreground">{transitions}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <CodeBlock title="Status Transition Diagram" code={`                    ┌─────────────┐
                    │   pending    │
                    └──────┬──────┘
                           │
              ┌────────────┼────────────┐
              ▼            ▼            ▼
       ┌────────────┐ ┌────────┐  ┌─────────┐
       │ partially_  │ │  paid  │  │ expired │
       │   paid      │ └────────┘  └─────────┘
       └──────┬──────┘
              │
       ┌──────┼──────┐
       ▼      ▼      ▼
  ┌────────┐ ┌────┐ ┌─────────┐
  │  paid  │ │over│ │ expired │
  └────────┘ │paid│ └─────────┘
             └────┘`} />
          </Section>

          {/* WEBHOOKS */}
          <Section id="webhooks" title="Webhooks & Notifications">
            <p>CoinPayUp dispatches webhook events for every significant payment lifecycle change. All webhooks are signed with HMAC-SHA256 using your webhook secret.</p>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Event Types</h3>
            <div className="overflow-x-auto my-4">
              <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                <thead><tr className="bg-muted/50">
                  <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Event</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Trigger</th>
                </tr></thead>
                <tbody>
                  {[
                    ['payment.confirmed', 'Any payment is confirmed on-chain for an invoice'],
                    ['invoice.paid', 'Invoice reaches paid status (fully settled)'],
                    ['invoice.underpaid', 'Partial payment detected — invoice is not fully paid'],
                    ['invoice.overpaid', 'Payment exceeds invoice amount by > 1%'],
                  ].map(([event, trigger], i) => (
                    <tr key={i}>
                      <td className="px-4 py-2.5 border-b border-border"><code className="px-1.5 py-0.5 rounded bg-muted text-xs font-mono text-foreground">{event}</code></td>
                      <td className="px-4 py-2.5 border-b border-border text-muted-foreground">{trigger}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Webhook Headers</h3>
            <CodeBlock title="Headers sent with every webhook" code={`Content-Type: application/json
X-CoinPayUp-Signature: <HMAC-SHA256 hex digest>
X-CoinPayUp-Event: payment.confirmed
User-Agent: CoinPayUp-Webhook/1.0`} />

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Sample Payloads</h3>
            <CodeBlock title="payment.confirmed" code={`{
  "event": "payment.confirmed",
  "data": {
    "invoice_id": "inv_abc123",
    "tx_hash": "0xdef456...",
    "amount": 100.0,
    "currency": "USDT",
    "confirmations": 12
  }
}`} />
            <CodeBlock title="invoice.underpaid" code={`{
  "event": "invoice.underpaid",
  "data": {
    "invoice_id": "inv_abc123",
    "amount_expected": 100.0,
    "amount_received": 60.0,
    "remaining_amount": 40.0,
    "currency": "USDT",
    "tx_hash": "0xdef456...",
    "message": "Partial payment received. 40.0 USDT remaining."
  }
}`} />
            <CodeBlock title="invoice.overpaid" code={`{
  "event": "invoice.overpaid",
  "data": {
    "invoice_id": "inv_abc123",
    "amount": 105.0,
    "currency": "USDT",
    "tx_hash": "0xdef456...",
    "overpaid_by": 5.0
  }
}`} />

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Signature Verification</h3>
            <CodeBlock title="Node.js Example" lang="javascript" code={`const crypto = require('crypto');

function verifyWebhook(payload, signature, secret) {
  const expected = crypto
    .createHmac('sha256', secret)
    .update(JSON.stringify(payload))
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(signature),
    Buffer.from(expected)
  );
}`} />
          </Section>

          {/* BEST PRACTICES */}
          <Section id="best-practices" title="Best Practices for Merchants">
            <div className="space-y-4">
              {[
                { title: 'Choose tolerance wisely', desc: 'Start with 1–2% for stablecoins. Monitor your payment success rate and adjust. Lower tolerance for high-value invoices.' },
                { title: 'Handle partial payments proactively', desc: 'Set up the invoice.underpaid webhook to notify your support team. Automate follow-up emails to customers with the remaining balance.' },
                { title: 'Set appropriate expiry times', desc: 'Default to 60 minutes. For high-value transactions, consider 120+ minutes to account for exchange withdrawal delays.' },
                { title: 'Always verify webhook signatures', desc: 'Never trust webhook payloads without verifying the HMAC-SHA256 signature. This prevents spoofed payment notifications.' },
                { title: 'Test in sandbox mode', desc: 'Use test mode to simulate all payment scenarios before going live. Create test invoices and use the simulate payment endpoint.' },
                { title: 'Monitor the dashboard', desc: 'Check unmatched payments and expired invoices regularly. These often indicate customer payment issues that need manual resolution.' },
                { title: 'Use round_up for unique amounts', desc: 'Enable the round_up verification method to generate unique payable amounts per invoice. This helps the blockchain listener automatically match payments to specific invoices.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-3 p-4 rounded-xl bg-muted/30 border border-border">
                  <CheckCircle className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">{item.title}</p>
                    <p className="text-sm text-muted-foreground mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* SECURITY */}
          <Section id="security" title="Security Considerations">
            <h3 className="text-lg font-semibold text-foreground mt-4 mb-3">Replay Attack Prevention</h3>
            <p>Every transaction hash is recorded in the payments database. A hash that has already been used — for the same invoice or any other — is rejected. This prevents replay attacks where an attacker tries to claim the same payment multiple times.</p>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Double-Spend Protection</h3>
            <p>CoinPayUp checks block confirmations before marking a payment as final. While a single confirmation is sufficient for most EVM chains (due to proof-of-stake finality), CoinPayUp records the confirmation count for audit purposes. The system also validates that the transaction receipt has <code className="px-1.5 py-0.5 rounded bg-muted text-foreground text-xs font-mono">status: 0x1</code>, rejecting any reverted or failed transactions.</p>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Trustless Verification</h3>
            <p>CoinPayUp does <strong className="text-foreground">not</strong> rely on any user-submitted payment amount. The amount is always decoded directly from the blockchain's Transfer event log data. This makes it cryptographically impossible to fake the payment amount — the data comes from the immutable transaction receipt.</p>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Cross-Invoice Deduplication</h3>
            <p>A single transaction can only pay one invoice. If a transaction hash has already been confirmed for invoice A, it cannot be submitted for invoice B. This prevents a scenario where a merchant with multiple pending invoices at the same wallet address could be defrauded by a single payment.</p>

            <InfoBox type="success" title="Summary">
              CoinPayUp's verification model is <strong>trustless by design</strong>: amounts are decoded from immutable on-chain data, recipients are validated against invoice records, duplicates are rejected across all invoices, and webhook signatures are HMAC-signed. No user input is trusted — every claim is verified against the blockchain.
            </InfoBox>
          </Section>

          <Section id="reference-field" title="Reference Field">
            <p>The <code className="px-1.5 py-0.5 rounded bg-muted text-foreground text-xs font-mono">reference</code> field is a free-form string you can attach to any invoice. Use it to store customer details, internal order metadata, or any additional context you need alongside a payment — without having to look it up in your own database.</p>

            <InfoBox type="info" title="What can you store in reference?">
              Anything meaningful to your business: customer name, email address, subscription tier, coupon codes, notes, or pipe-delimited key-value pairs. The field accepts up to 1000 characters of plain text.
            </InfoBox>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Creating an Invoice with reference</h3>
            <CodeBlock title="POST /api/invoices — with reference" code={`curl -X POST https://coinpayup.com/api/invoices \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount": 49.99,
    "currency": "USDT",
    "description": "Premium Plan — Monthly",
    "external_id": "ORDER-2026-00789",
    "reference": "Customer: John Doe | Email: john@example.com | Plan: Premium"
  }'`} lang="bash" />

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Common reference Patterns</h3>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Use Case</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Example Value</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Customer name + email', 'Customer: Jane Smith | Email: jane@example.com'],
                    ['Order metadata', 'Order: #1042 | Items: 3 | Coupon: SAVE10'],
                    ['Subscription info', 'Plan: Gold | Billing: Monthly | Seats: 5'],
                    ['WooCommerce order', 'WC Order #1234 | Status: Processing | User ID: 567'],
                    ['Shopify checkout', 'Shopify: #5001 | Shop: mystore.myshopify.com'],
                    ['Custom JSON-style', '{"user_id":42,"tier":"pro","campaign":"summer26"}'],
                  ].map(([useCase, example], i) => (
                    <tr key={i}>
                      <td className="px-4 py-2.5 border-b border-border text-muted-foreground text-xs font-medium">{useCase}</td>
                      <td className="px-4 py-2.5 border-b border-border font-mono text-primary text-xs">{example}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">reference in Webhook Payloads</h3>
            <p>The <code className="px-1.5 py-0.5 rounded bg-muted text-foreground text-xs font-mono">reference</code> field is included in every webhook event so your server receives the full context without any extra lookups.</p>
            <CodeBlock title="Webhook payload (payment.confirmed)" code={`{
  "event": "payment.confirmed",
  "invoice_id": "INV-20260221-X7K9P2",
  "status": "paid",
  "amount_paid": 49.99,
  "currency": "USDT",
  "external_id": "ORDER-2026-00789",
  "reference": "Customer: John Doe | Email: john@example.com | Plan: Premium",
  "tx_hash": "0xabc123...",
  "confirmed_at": "2026-02-21T14:32:00Z"
}`} />

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Full Invoice Fields Reference</h3>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Field</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Type</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Required</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['amount', 'number', true, 'Payment amount in the specified fiat currency'],
                    ['currency', 'string', true, 'Fiat currency code: USDT, USD, EUR, etc.'],
                    ['network', 'string', false, 'Blockchain: bsc, tron, polygon, ethereum, etc.'],
                    ['description', 'string', false, 'Human-readable label shown on the checkout page'],
                    ['customer_email', 'string', false, 'Customer email — triggers payment notification emails'],
                    ['external_id', 'string', false, 'Your internal order/reference ID for reconciliation'],
                    ['reference', 'string', false, 'Free-form field — customer name, email, notes, JSON, pipe-delimited data. Max 1000 chars. Returned in webhooks and API responses.'],
                    ['success_url', 'string', false, 'Redirect URL after successful payment'],
                    ['cancel_url', 'string', false, 'Redirect URL after invoice expiry or cancellation'],
                    ['expiry_minutes', 'number', false, 'Invoice expiry window in minutes (default: 30)'],
                  ].map(([field, type, required, desc], i) => (
                    <tr key={i}>
                      <td className="px-4 py-2.5 border-b border-border font-mono text-xs">
                        <span className={field === 'reference' ? 'text-primary font-bold' : 'text-foreground'}>{field}</span>
                        {field === 'reference' && <span className="ml-1.5 text-[10px] bg-primary/15 text-primary px-1.5 py-0.5 rounded-full font-sans">new</span>}
                      </td>
                      <td className="px-4 py-2.5 border-b border-border text-muted-foreground text-xs">{type}</td>
                      <td className="px-4 py-2.5 border-b border-border text-xs">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${required ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                          {required ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 border-b border-border text-muted-foreground text-xs">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <InfoBox type="success" title="Best Practice">
              Store at minimum the <strong>customer email</strong> in <code className="text-xs font-mono">reference</code> so you can instantly identify who made a payment from your webhook handler, even without querying your own database.
            </InfoBox>
          </Section>

        </main>
      </div>

      <SiteFooter />
    </div>
  );
};

export default DocsPage;
